import { TRPCClientError } from '@trpc/client';

/**
 * Error thrown when a user is not allowed to perform an action.
 * This error should be caught and handled by the application.
 */
export class ForbiddenError extends Error {
  status = 403;

  constructor(message?: string) {
    super(message);

    this.name = 'ForbiddenError';
  }
}

/**
 * App-level not-found error (mirrors ForbiddenError for loaders/guards).
 * Prefer throwing this when a route intentionally maps a missing entity to 404.
 */
export class NotFoundError extends Error {
  status = 404;

  constructor(message?: string) {
    super(message);

    this.name = 'NotFoundError';
  }
}

type TrpcErrorData = {
  code?: string;
  httpStatus?: number;
};

function hasNotFoundTrpcData(data: TrpcErrorData | undefined): boolean {
  return data?.code === 'NOT_FOUND' || data?.httpStatus === 404;
}

/**
 * True for app NotFoundError or TRPC NOT_FOUND / HTTP 404 client errors.
 * Used by the root ErrorPage so entity misses render as 404 instead of 500.
 */
export function isNotFoundError(error: unknown): boolean {
  if (error instanceof NotFoundError) {
    return true;
  }

  if (error instanceof TRPCClientError) {
    const data = error.data as TrpcErrorData | undefined;
    const shapeData =
      'shape' in error
        ? (error.shape as { data?: TrpcErrorData } | null | undefined)?.data
        : undefined;

    return hasNotFoundTrpcData(data) || hasNotFoundTrpcData(shapeData);
  }

  if (error instanceof Error && error.cause != null) {
    return isNotFoundError(error.cause);
  }

  return false;
}
